import { useEffect, useLayoutEffect, useState, useCallback, useRef, useMemo, type PointerEvent as ReactPointerEvent } from 'react';
import { cn } from '@/lib/utils';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Plus,
  MoreVertical,
  ArrowUpCircle,
  ArrowDownCircle,
  ArrowLeftRight,
  Pencil,
  Trash2,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  X,
  Layers,
  Eye,
  Copy,
  Search,
  Download,
  Hash,
  RotateCcw,
  BarChart2,
  AlertCircle,
  CreditCard,
  FileText,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { ToastAction } from '@/components/ui/toast';
import { useAppDispatch, useAppSelector } from '@/features/hooks';
import { useFABContext } from '@/components/common/FABContext';
import { useCurrency } from '@/hooks/useCurrency';
import { useTimezone } from '@/hooks/useTimezone';
import { useItemCountPreference } from '@/hooks/useItemCountPreference';
import { fetchExtensions } from '@/features/extensions/extensionsSlice';
import {
  fetchTransactions,
  createTransaction,
  updateTransaction,
  deleteTransaction,
  transferBetweenAccounts,
  duplicateTransaction,
  setPage,
  setPageSize,
} from '@/features/transactions/transactionsSlice';
import { linkReceipt } from '@/features/receipts/receiptsSlice';
import { fetchAccounts } from '@/features/accounts/accountsSlice';
import { createCategory, deleteCategory, fetchCategories, updateCategory } from '@/features/categories/categoriesSlice';
import TransactionForm from '@/components/transactions/TransactionForm';
import type { TransactionFormData } from '@/components/transactions/TransactionForm';
import { useModalStack } from '@/hooks/useModalStack';
import type { ModalEntry } from '@/hooks/useModalStack';
import ReceiptScannerDialog from '@/components/transactions/ReceiptScannerDialog';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import type { FilterState } from '@/lib/filters';
import { getDateRangeFromFilter } from '@/lib/filters';
import { UnifiedTopBar } from '@/components/transactions/UnifiedTopBar';
import { useFilterUrlSync } from '@/hooks/useFilterUrlSync';
import CategoryDetailSheet from '@/components/categories/CategoryDetailSheet';
import TransactionDetailSheet from '@/components/transactions/TransactionDetailSheet';
import { TagDetailSheet } from '@/components/transactions/TagDetailSheet';
import { TransactionInsightsCard } from '@/components/transactions/TransactionInsightsCard';
import { TransactionExportDialog } from '@/components/transactions/ExportDialog';
import { CategoryForm, type CategoryFormValues } from '@/components/categories/CategoryForm';
import PageGuide from '@/components/common/PageGuide';
import { transactionsGuide } from '@/components/common/guideContent';
import PageSizeSelector from '@/components/common/PageSizeSelector';
import { IconButton } from '@/components/ui/icon-button';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useTransactionColumns } from '@/components/transactions/TransactionColumnSelector';
import type { TransactionColumnId } from '@/components/transactions/TransactionColumnSelector';
import { Checkbox } from '@/components/ui/checkbox';
import TransactionLineItems from '@/components/transactions/TransactionLineItems';
// T146: Mobile bottom-sheet for receipt-style line items.
import MobileItemsSheet from '@/components/transactions/MobileItemsSheet';
import { BatchActionsToolbar } from '@/components/transactions/batch/BatchActionsToolbar';
import { BatchUpdateDialog } from '@/components/transactions/batch/BatchUpdateDialog';
import { BatchDeleteDialog } from '@/components/transactions/batch/BatchDeleteDialog';
import { BatchApplyRuleDialog } from '@/components/transactions/batch/BatchApplyRuleDialog';
import { BatchTagDialog } from '@/components/transactions/batch/BatchTagDialog';
import type { Transaction, Category, TransactionCategory, TransactionTemplate } from '@/lib/types';
import { getGroupSignedNetTotal, getTransactionAmountBreakdown, toStableGroupKey } from '@/lib/transactionAmounts';
import { useOfflineSync } from '@/contexts/OfflineSyncContext';
import { addOptimisticTransaction } from '@/features/transactions/transactionsSlice';
import { fetchTemplates, deleteTemplate } from '@/features/templates/templatesSlice';
import { TemplateList } from '@/components/templates/TemplateList';
import { TemplateForm } from '@/components/templates/TemplateForm';
import TemplateDetailSheet from '@/components/templates/TemplateDetailSheet';
import { QuickCreateCard } from '@/components/templates/QuickCreateCard';
import api from '@/services/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

const typeConfig = {
  income: { icon: ArrowUpCircle, color: 'text-green-600', badge: 'bg-green-100 text-green-800' },
  expense: { icon: ArrowDownCircle, color: 'text-red-600', badge: 'bg-red-100 text-red-800' },
  transfer: { icon: ArrowLeftRight, color: 'text-blue-600', badge: 'bg-blue-100 text-blue-800' },
};

// ── Tag color helper [T122] ──────────────────────────────────────────────────
const TAG_COLORS = ['#6366f1','#ec4899','#f59e0b','#10b981','#3b82f6','#ef4444','#8b5cf6','#14b8a6'];
function getTagColor(tag: string): string {
  let hash = 0;
  for (let i = 0; i < tag.length; i++) {
    hash = (hash * 31 + tag.charCodeAt(i)) | 0;
  }
  return TAG_COLORS[Math.abs(hash) % TAG_COLORS.length];
}

function intersectDateRange(
  range: { dateFrom?: string; dateTo?: string },
  day: string,
): { dateFrom: string; dateTo: string } | null {
  const dayStart = new Date(`${day}T00:00:00`);
  const dayEnd = new Date(`${day}T23:59:59.999`);
  const rangeStart = range.dateFrom ? new Date(range.dateFrom) : null;
  const rangeEnd = range.dateTo ? new Date(range.dateTo) : null;
  if ((rangeStart && dayEnd < rangeStart) || (rangeEnd && dayStart > rangeEnd)) return null;
  const start = rangeStart && rangeStart > dayStart ? rangeStart : dayStart;
  const end = rangeEnd && rangeEnd < dayEnd ? rangeEnd : dayEnd;
  return { dateFrom: start.toISOString(), dateTo: end.toISOString() };
}



export default function Transactions() {
  useDocumentTitle('Transactions');
  const dispatch = useAppDispatch();
  const { toast } = useToast();
  const { fmt } = useCurrency();
  const { timezone, formatInUserTimezone } = useTimezone();
  const { isOnline, addToQueue } = useOfflineSync();


  // [T114-F6-b] URL ↔ filter sync
  const { filtersFromUrl, syncFiltersToUrl } = useFilterUrlSync();

  // [T128] Subscription deep-link: read ?subscriptionId= from URL
  const [searchParams, setSearchParams] = useSearchParams();
  const subscriptionIdFromUrl = searchParams.get('subscriptionId');

  // [Phase5] URL-based pill tab: ?tab=transactions (default) | ?tab=templates
  const activeTab = searchParams.get('tab') || 'transactions';

  const { transactions, isLoading, pagination, error: transactionsError, dateBounds } = useAppSelector(
    (state) => state.transactions
  );
  // [T-76e90519] Real transaction date bounds are server-computed
  // (GET /transactions now returns a `dateBounds: { min, max }` field that
  // spans ALL matching transactions for the current user + non-date filters,
  // excluding the active `dateFrom`/`dateTo` predicate). Threading the global
  // bounds into the date range control keeps the All range truthful across
  // every page size — the previous page-local derivation showed stale
  // page-bound endpoints once the dataset exceeded the API limit (20). The
  // bounds object keeps stable identity until Redux re-emits, so the
  // downstream useMemo in TransactionDateRangeControl stays memoized.
  const transactionDateBounds = useMemo(() => {
    if (!dateBounds || (!dateBounds.min && !dateBounds.max)) return undefined;
    const min = dateBounds.min ? new Date(dateBounds.min) : undefined;
    const max = dateBounds.max ? new Date(dateBounds.max) : undefined;
    if (!min || !max) return undefined;
    if (Number.isNaN(min.getTime()) || Number.isNaN(max.getTime())) return undefined;
    return { from: min, to: max };
  }, [dateBounds]);
  const { accounts } = useAppSelector((state) => state.accounts);
  const { categories } = useAppSelector((state) => state.categories);
  // [Phase E] Transaction Add/Detail/Edit tri-state replaced by a modal stack.
  // The top entry is the only transaction modal rendered. Push always appends
  // (so Detail survives beneath a shortcut Edit), pop drops the top, replace
  // swaps the top (used by footer Edit which closes Detail first).
  const modalStack = useModalStack();
  const topEntry: ModalEntry | null = modalStack.top;

  // Derived: transactionId for the current Detail entry, or null when Edit/Add is on top.
  const detailEntryTransactionId = topEntry?.kind === 'detail' ? topEntry.transactionId : null;
  // Derived: transactionId for the current Edit entry, or null otherwise.
  const editEntryTransactionId = topEntry?.kind === 'edit' ? topEntry.transactionId : null;

  // [T-FAB] Register quick-add action so the global FAB can open this form
  const { registerFABActions } = useFABContext();
  useEffect(() => {
    registerFABActions({ onAddTransaction: () => { modalStack.push({ kind: 'add' }); } });
    return () => registerFABActions({});
  }, [registerFABActions, modalStack]);

  // [T-FAB-DRAFT] Auto-open add form when navigating via a draft link from FAB
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if ((location.state as { openForm?: boolean } | null)?.openForm) {
      modalStack.push({ kind: 'add' });
      navigate(location.pathname + location.search, { replace: true, state: null });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, modalStack]);

  const [deletingId, setDeletingId] = useState<string | null>(null);
  // [T95P2] Restore persisted view state
  const [showInsights, setShowInsights] = useState(() => {
    try {
      const stored = localStorage.getItem('fintrack:txn-insights-tab');
      return stored ? JSON.parse(stored) === true : false;
    } catch { return false; }
  });

  // [T-FLOATING-BAR-TOGGLE-PY] Secondary floating-bar minimization — page-owned
  // persistence keeps the primary action row available while secondary content
  // collapses behind the persistent restore affordance.
  const [isTopBarHidden, setIsTopBarHidden] = useState<boolean>(() => {
    try {
      return localStorage.getItem('fintrack:txn-topbar-hidden') === 'true';
    } catch {
      return false;
    }
  });

  const [calendarDayFilter, setCalendarDayFilter] = useState<string | null>(null);
  const calendarDayFocusRef = useRef<(() => void) | null>(null);
  const clearCalendarDayFilter = useCallback((reason?: 'outside-range') => {
    setCalendarDayFilter(null);
    const returnFocus = calendarDayFocusRef.current;
    calendarDayFocusRef.current = null;
    if (returnFocus) requestAnimationFrame(returnFocus);
    if (reason === 'outside-range') {
      toast({
        title: 'Calendar filter cleared',
        description: 'The selected day is outside the active date range.',
      });
    }
  }, [toast]);
  const handleCalendarDayFilter = useCallback((date: string, returnFocus: () => void) => {
    calendarDayFocusRef.current = returnFocus;
    setCalendarDayFilter(date);
    dispatch(setPage(1));
  }, [dispatch]);

  const [filters, setFilters] = useState<FilterState>(() => {
    // [T114-F6-b] Priority: URL params > localStorage > defaults
    if (filtersFromUrl) return filtersFromUrl;
    try {
      const stored = localStorage.getItem('fintrack:txn-filters');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (typeof parsed === 'object' && parsed !== null) {
          // Re-hydrate date fields: JSON.parse returns strings, not Date objects
          if (parsed.dateFrom) parsed.dateFrom = new Date(parsed.dateFrom);
          if (parsed.dateTo) parsed.dateTo = new Date(parsed.dateTo);
          return parsed;
        }
      }
    } catch { /* ignore */ }
    return { period: '30d' };
  });
  const activeDateRange = useMemo(
    () => getDateRangeFromFilter(filters.period, filters.dateFrom, filters.dateTo, timezone),
    [filters, timezone],
  );
  const effectiveCalendarDateRange = useMemo(
    () => calendarDayFilter ? intersectDateRange(activeDateRange, calendarDayFilter) : activeDateRange,
    [activeDateRange, calendarDayFilter],
  );
  useEffect(() => {
    if (calendarDayFilter && !effectiveCalendarDateRange) {
      clearCalendarDayFilter('outside-range');
    }
  }, [calendarDayFilter, clearCalendarDayFilter, effectiveCalendarDateRange]);
  useEffect(() => {
    if (!showInsights && calendarDayFilter) clearCalendarDayFilter();
  }, [calendarDayFilter, clearCalendarDayFilter, showInsights]);
  useEffect(() => {
    if (!calendarDayFilter) return undefined;
    const frame = requestAnimationFrame(() => {
      document.querySelector('[data-testid="calendar-day-filter-chip"]')?.scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [calendarDayFilter]);
  useEffect(() => {
    if (!calendarDayFilter) return undefined;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') clearCalendarDayFilter();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [calendarDayFilter, clearCalendarDayFilter]);
  const [formLoading, setFormLoading] = useState(false);
  const { isVisible: isColVisible, visibility: colVisibility, toggleColumn, resetColumns, restoreColumns } = useTransactionColumns();

  // Receipt scanner (independent of the transaction modal stack — scanner state lives on its own)
  const [showScanner, setShowScanner] = useState(false);

  // [Phase E] Scanner result is now carried by an {kind:'add', initialValues} stack entry,
  // so no separate `scannedDefaults` page state is required.
  const handleScanned = (data: Partial<TransactionFormData>) => {
    modalStack.push({ kind: 'add', initialValues: data });
  };

  // [Phase E] Detail sheet is driven entirely by the stack. The Detail entry's
  // transactionId is resolved to the latest Redux snapshot so live updates
  // (e.g. receipt uploads via onRefresh) propagate to the open sheet.
  const liveDetailTransaction = useMemo(() => {
    if (!detailEntryTransactionId) return null;
    return transactions.find((t) => t.id === detailEntryTransactionId) ?? null;
  }, [transactions, detailEntryTransactionId]);

  // [Phase E] Edit form's transaction is likewise resolved from the Redux list
  // so the Edit entry always reflects the latest server data when the Edit
  // entry is pushed on top of an existing Detail entry.
  const editingTransaction = useMemo(() => {
    if (!editEntryTransactionId) return null;
    return transactions.find((t) => t.id === editEntryTransactionId) ?? null;
  }, [transactions, editEntryTransactionId]);

  // [Phase E] Scroll-capture + push helper — shared by the row shortcut (amount/notes)
  // and the Detail footer Edit. Walks the stack to find the existing detail entry, captures
  // the deepest scrollable [role="dialog"] descendant's scrollTop into the detail entry's meta,
  // then pushes an edit entry so the form-back-btn can return to Detail with the original viewport
  // position. Used by both shortcut Edit (`onEditShortcut`) and footer Edit (`onEdit`) so the
  // round-trip contract is identical regardless of the entry point.
  const lastRestoredForRef = useRef<string | null>(null);
  const captureScrollAndPushEdit = useCallback((transactionId: string, fieldKey?: string) => {
    const stack = modalStack.stack;
    let detailIndex = -1;
    for (let i = stack.length - 1; i >= 0; i--) {
      const entry = stack[i];
      if (entry && entry.kind === 'detail' && entry.transactionId === transactionId) {
        detailIndex = i;
        break;
      }
    }
    if (detailIndex === -1) return;
    // Capture scroll position from the explicit detail-scroll-container via a
    // direct named querySelector (no generic descendant walk). This keeps the
    // tap-to-edit click handler free of synchronous layout walks that were
    // the latency bottleneck identified in the rev17 trace.
    const scrollContainer = document.querySelector('[data-testid="detail-scroll-container"]');
    const scrollY =
      scrollContainer instanceof HTMLElement ? scrollContainer.scrollTop : 0;

    modalStack.patchMeta(detailIndex, { scrollY });
    modalStack.push({ kind: 'edit', transactionId, ...(fieldKey !== undefined && { focusField: fieldKey }) });
  }, [modalStack]);

  const handleDetailEditShortcut = useCallback((fieldKey: string) => {
    const stack = modalStack.stack;
    for (let i = stack.length - 1; i >= 0; i--) {
      const entry = stack[i];
      if (entry && entry.kind === 'detail') {
        captureScrollAndPushEdit(entry.transactionId, fieldKey);
        return;
      }
    }
  }, [modalStack, captureScrollAndPushEdit]);

  // [rev22 G3] After pop, if the new top is a Detail entry whose meta.scrollY was
  // captured before the shortcut Edit, restore it directly to the named
  // detail-scroll-container (not a generic deepest-scrollable walk) and clamp
  // the saved value to the container's current scroll range. Radix
  // SheetContent is portal-mounted after this layout effect runs, so we defer
  // the actual DOM lookup to the next animation frame (one-shot, no polling)
  // and cancel the pending rAF in cleanup if the effect re-runs.
  //
  // Async content expansion: the Detail body is mounted with placeholder
  // height and grows after first paint (e.g. TabsContent defers child rows).
  // A single rAF can therefore observe a too-small scrollHeight and clamp
  // the saved 240px down to a smaller value before the content arrives.
  // The guard `lastRestoredForRef.current` is only committed in two explicit
  // conditions:
  //   1. `maxScroll >= targetScrollY` (the saved position is reachable):
  //      apply the full target and commit the guard.
  //   2. The current modal-stack top is no longer this Detail entry
  //      (isCurrentTop() returns false): cleanup without committing.
  //
  // When `maxScroll < targetScrollY` (content too short to reach the saved
  // position), we apply the current clamped max and KEEP THE OBSERVER ALIVE.
  // A later content growth tick can then reapply once the target becomes
  // reachable. We do NOT commit the guard while the target is unreachable;
  // this prevents the prior bug where a two-equal-scrollHeight stability
  // rule reached its commit threshold on the initial pre-expansion height
  // and disconnected the observer before async content could arrive.
  //
  // For the shorter-fixture case (content never grows enough), the observer
  // simply stops firing once content is stable; the user navigates away,
  // isCurrentTop() returns false, and cleanup disconnects the observer.
  useLayoutEffect(() => {
    const top = modalStack.top;
    if (!top || top.kind !== 'detail') {
      lastRestoredForRef.current = null;
      return;
    }
    const targetScrollY = top.meta?.scrollY;
    if (typeof targetScrollY !== 'number') return;
    if (lastRestoredForRef.current === top.transactionId) return;

    const targetTransactionId = top.transactionId;
    let cancelled = false;
    let rafId: number | null = null;
    let resizeObserver: ResizeObserver | null = null;

    const isCurrentTop = () => {
      const currentTop = modalStack.top;
      return (
        currentTop !== null &&
        currentTop.kind === 'detail' &&
        currentTop.transactionId === targetTransactionId
      );
    };

    const cleanup = () => {
      cancelled = true;
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      if (resizeObserver !== null) {
        resizeObserver.disconnect();
        resizeObserver = null;
      }
    };

    // Single apply: write the target if reachable, otherwise write the clamped
    // max. The guard is committed only on the reachable branch; the unreachable
    // branch leaves the guard unset so later content growth can reapply.
    const apply = () => {
      if (cancelled || !isCurrentTop()) {
        cleanup();
        return;
      }
      const scrollContainer = document.querySelector('[data-testid="detail-scroll-container"]');
      if (!(scrollContainer instanceof HTMLElement)) return;
      const currentScrollHeight = scrollContainer.scrollHeight;
      const maxScroll = Math.max(0, currentScrollHeight - scrollContainer.clientHeight);

      if (maxScroll >= targetScrollY) {
        // Target reachable: write the full saved position, commit the guard,
        // and disconnect. Any later tick on the same transaction is blocked
        // by the early `lastRestoredForRef.current === top.transactionId` guard.
        if (scrollContainer.scrollTop !== targetScrollY) {
          scrollContainer.scrollTop = targetScrollY;
        }
        lastRestoredForRef.current = targetTransactionId;
        cleanup();
        return;
      }

      // Target unreachable on this tick: write the current clamped max and
      // keep the observer alive. A later async content growth tick will fire
      // apply() again with a larger maxScroll, hit the reachable branch,
      // and commit. If the fixture is genuinely short and content never
      // grows, the observer stays quiet after the initial callback and
      // cleanup runs when the user navigates away.
      const desired = Math.max(0, maxScroll);
      if (scrollContainer.scrollTop !== desired) {
        scrollContainer.scrollTop = desired;
      }
    };

    // First tick: wait one rAF so Radix SheetContent has a chance to mount,
    // then set up the observer + apply once. The ResizeObserver (or fallback)
    // drives subsequent re-applies whenever content grows.
    rafId = requestAnimationFrame(() => {
      rafId = null;
      if (cancelled) return;
      const scrollContainer = document.querySelector('[data-testid="detail-scroll-container"]');
      if (!(scrollContainer instanceof HTMLElement)) {
        // Container not yet mounted; the next render of this effect will retry
        // (the modal-stack.top dep drives re-runs). Bail without committing
        // the guard so the retry can attempt again.
        return;
      }
      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(() => {
          apply();
        });
        // Observe the scroll container (fires on its own layout size change)
        // and the first content child (fires when the inner content grows,
        // which can change scrollHeight without changing clientHeight).
        // The Detail body's TabsContent wraps the field grid in a single
        // child div, so observing the firstElementChild catches the async
        // expansion that does not surface on the container itself.
        resizeObserver.observe(scrollContainer);
        const contentChild = scrollContainer.firstElementChild;
        if (contentChild instanceof HTMLElement) {
          resizeObserver.observe(contentChild);
        }
      } else {
        // Fallback for environments without ResizeObserver: apply once with
        // clamping and clean up. We do not commit the guard because we have
        // no way to detect later content growth. This fallback cannot mask
        // a supported path because supported paths (modern Chromium) always
        // expose ResizeObserver.
        apply();
        cleanup();
      }
      // Apply once immediately to seed the initial position. The ResizeObserver
      // (if available) will fire its own callback asynchronously with the
      // same initial height, which is a harmless no-op under the unreachable
      // branch (scrollTop is already at the desired value).
      apply();
    });

    return cleanup;
  }, [modalStack.top]);

  // [rev22 G3] Fresh Detail entries (no captured scrollY in meta) must open at
  // scrollTop=0. Defensive zeroing prevents stale scroll from leaking across
  // navigation if the underlying element is reused by the portal lifecycle.
  // This effect only runs when meta.scrollY is absent; the restoration effect
  // above owns the captured value.
  useLayoutEffect(() => {
    const top = modalStack.top;
    if (!top || top.kind !== 'detail') return;
    if (typeof top.meta?.scrollY === 'number') return;
    const targetTransactionId = top.transactionId;
    const rafId = requestAnimationFrame(() => {
      const currentTop = modalStack.top;
      if (!currentTop || currentTop.kind !== 'detail' || currentTop.transactionId !== targetTransactionId) return;
      const scrollContainer = document.querySelector('[data-testid="detail-scroll-container"]');
      if (scrollContainer instanceof HTMLElement) {
        scrollContainer.scrollTop = 0;
      }
    });
    return () => cancelAnimationFrame(rafId);
  }, [modalStack.top]);

  // [T84P2] Category detail sheet state
  const [detailCategoryId, setDetailCategoryId] = useState<string | null>(null);
  const [categoryDetailOpen, setCategoryDetailOpen] = useState(false);

  // T146: Mobile items bottom-sheet — replaces inline expansion under mobile cards.
  const [mobileItemsTransaction, setMobileItemsTransaction] = useState<Transaction | null>(null);
  const handleOpenMobileItems = (txn: Transaction) => setMobileItemsTransaction(txn);
  const [isDesktopInlineItems, setIsDesktopInlineItems] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches,
  );

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const media = window.matchMedia('(min-width: 768px)');
    const handleChange = (event: MediaQueryListEvent) => setIsDesktopInlineItems(event.matches);
    setIsDesktopInlineItems(media.matches);
    media.addEventListener('change', handleChange);
    return () => media.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    if (isDesktopInlineItems && mobileItemsTransaction !== null) {
      setMobileItemsTransaction(null);
    }
  }, [isDesktopInlineItems, mobileItemsTransaction]);

  // [T60P0] Group By state — [T69P1-a] default to 'date' for immediate usefulness
  type GroupByOption = 'none' | 'date' | 'type' | 'category' | 'account';
  const [groupBy, setGroupBy] = useState<GroupByOption>(() => {
    const stored = localStorage.getItem('fintrack:txn-group-by');
    if (stored && ['none', 'date', 'type', 'category', 'account'].includes(stored)) {
      return stored as GroupByOption;
    }
    return 'date';
  });
  // Category CRUD state [T45P0 — categories moved from Accounts]
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [isSubmittingCategory, setIsSubmittingCategory] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null);

  // [T84P2-c] Category search & type filter
  const [categorySearchQuery, setCategorySearchQuery] = useState('');
  const [categoryTypeFilter, setCategoryTypeFilter] = useState<'all' | 'income' | 'expense'>('all');

  const { isLoading: categoriesLoading } = useAppSelector((state) => state.categories);
  const showLineItems = useAppSelector(state => state.auth.user?.preferences?.showLineItemsInList ?? false);
  const { showItemCount, setItemCount } = useItemCountPreference();

  // [T84P2-c] Filtered categories
  const filteredCategories = useMemo(() => {
    let result = categories;
    if (categoryTypeFilter !== 'all') {
      result = result.filter((c) => c.type === categoryTypeFilter);
    }
    if (categorySearchQuery.trim()) {
      const q = categorySearchQuery.toLowerCase();
      result = result.filter((c) => c.name.toLowerCase().includes(q));
    }
    return result;
  }, [categories, categoryTypeFilter, categorySearchQuery]);

  // [T122] Tag management state
  const [tagSearchQuery, setTagSearchQuery] = useState('');
  const [renamingTag, setRenamingTag] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [tagToDelete, setTagToDelete] = useState<string | null>(null);
  const [tagStats, setTagStats] = useState<{ tag: string; count: number; amount: number }[]>([]);
  const [tagStatsLoading, setTagStatsLoading] = useState(false);
  // [T123] Tag create + detail state
  const [creatingTag, setCreatingTag] = useState(false);
  const [createTagValue, setCreateTagValue] = useState('');
  const [detailTag, setDetailTag] = useState<string | null>(null);
  const [tagDetailOpen, setTagDetailOpen] = useState(false);

  // [T122] Filtered tag stats
  const filteredTagStats = useMemo(() => {
    if (!tagSearchQuery.trim()) return tagStats;
    const q = tagSearchQuery.toLowerCase();
    return tagStats.filter((s) => s.tag.toLowerCase().includes(q));
  }, [tagStats, tagSearchQuery]);

  // [T114-F5-e] Multi-select state for batch operations
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectionMode, setSelectionMode] = useState(false);
  const [showBatchUpdate, setShowBatchUpdate] = useState(false);
  const [showBatchDelete, setShowBatchDelete] = useState(false);
  const [showBatchApplyRule, setShowBatchApplyRule] = useState(false);
  const [showBatchTag, setShowBatchTag] = useState(false);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectGroup = useCallback((groupTxnIds: string[]) => {
    setSelectedIds((prev) => {
      const allSelected = groupTxnIds.every((id) => prev.has(id));
      const next = new Set(prev);
      if (allSelected) {
        groupTxnIds.forEach((id) => next.delete(id));
      } else {
        groupTxnIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds((prev) => (prev.size === 0 ? prev : new Set()));
  }, []);

  const clearSelectionAndExitMode = useCallback(() => {
    setSelectionMode(false);
    setSelectedIds((prev) => (prev.size === 0 ? prev : new Set()));
  }, []);

  const toggleSelectionMode = useCallback(() => {
    if (selectionMode) {
      clearSelectionAndExitMode();
      return;
    }
    setSelectionMode(true);
  }, [selectionMode, clearSelectionAndExitMode]);

  const handleLongPressSelect = useCallback((id: string) => {
    setSelectionMode(true);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Collect all existing tags for autocompletion — sourced from tagStats (system-wide, not page-local)
  const allTags = useMemo(() => {
    const unique = new Map<string, string>();
    tagStats.forEach((s) => unique.set(s.tag.toLowerCase(), s.tag));
    return [...unique.values()].sort((a, b) => a.localeCompare(b));
  }, [tagStats]);

  // Auto-enter selection mode when selection appears from non-mode sources
  useEffect(() => {
    if (!selectionMode && selectedIds.size > 0) {
      setSelectionMode(true);
    }
  }, [selectionMode, selectedIds]);

  // Clear selection intent when table context changes.
  useEffect(() => {
    clearSelectionAndExitMode();
  }, [filters, pagination.currentPage, pagination.pageSize, groupBy, activeTab, clearSelectionAndExitMode]);

  // Clear selection when transactions change (page/filter fetch, delete, etc.)
  const prevTransactionIdsRef = useRef<string>('');
  useEffect(() => {
    const ids = transactions.map((t) => t.id).join(',');
    if (ids !== prevTransactionIdsRef.current) {
      prevTransactionIdsRef.current = ids;
      clearSelectionAndExitMode();
    }
  }, [transactions, clearSelectionAndExitMode]);

  // [T95P2] Persist view state changes
  useEffect(() => {
    localStorage.setItem('fintrack:txn-insights-tab', JSON.stringify(showInsights));
  }, [showInsights]);

  // [T-FLOATING-BAR-TOGGLE-PY] Persist secondary floating-bar minimization
  useEffect(() => {
    try {
      localStorage.setItem('fintrack:txn-topbar-hidden', isTopBarHidden ? 'true' : 'false');
    } catch {
      // localStorage unavailable — silently no-op
    }
  }, [isTopBarHidden]);

  useEffect(() => {
    localStorage.setItem('fintrack:txn-filters', JSON.stringify(filters));
    // [T114-F6-b] Sync filters to URL (debounced)
    syncFiltersToUrl(filters);
  }, [filters, syncFiltersToUrl]);

  useEffect(() => {
    localStorage.setItem('fintrack:txn-group-by', groupBy);
  }, [groupBy]);

  // [T95P2] Scroll position persistence (sessionStorage, per-tab)
  useEffect(() => {
    const scrollEl = document.querySelector('main');
    if (!scrollEl) return;
    const savedScroll = sessionStorage.getItem('fintrack:txn-scroll');
    if (savedScroll) {
      requestAnimationFrame(() => scrollEl.scrollTo(0, parseInt(savedScroll, 10)));
    }
    let timer: ReturnType<typeof setTimeout>;
    const onScroll = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        sessionStorage.setItem('fintrack:txn-scroll', String(scrollEl.scrollTop));
      }, 500);
    };
    scrollEl.addEventListener('scroll', onScroll, { passive: true });
    return () => { clearTimeout(timer); scrollEl.removeEventListener('scroll', onScroll); };
  }, []);

  // [T125-F5-c] Undo ref for Reset View (extended for [T-FLOATING-BAR-TOGGLE-PY])
  const previousViewStateRef = useRef<{
    filters: FilterState;
    showInsights: boolean;
    groupBy: string;
    colVisibility: Record<TransactionColumnId, boolean>;
    showItemCount: boolean;
    isTopBarHidden: boolean;
  } | null>(null);

  const handleResetVisibleDetails = useCallback(() => {
    resetColumns();
    setItemCount(true);
  }, [resetColumns, setItemCount]);

  // [T95P2] Reset view to defaults and restore the centered minimized toggle position.
  const handleResetView = useCallback(() => {
    // Save current state for undo
    previousViewStateRef.current = {
      filters: { ...filters },
      showInsights,
      groupBy,
      colVisibility: { ...colVisibility },
      showItemCount,
      isTopBarHidden,
    };
    setShowInsights(false);
    setIsTopBarHidden(false);
    setFilters({ period: '30d' });
    setGroupBy('date');
    handleResetVisibleDetails();
    dispatch(setPage(1));
    localStorage.removeItem('fintrack:txn-insights-tab');
    localStorage.removeItem('fintrack:txn-tags-panel');
    localStorage.removeItem('fintrack:txn-filters');
    localStorage.removeItem('fintrack:txn-group-by');
    localStorage.removeItem('fintrack:txn-visible-columns');
    localStorage.removeItem('fintrack:show-item-count');
    localStorage.removeItem('fintrack:txn-topbar-hidden');
    localStorage.removeItem('fintrack:txn-topbar-droplet-x');
    sessionStorage.removeItem('fintrack:txn-scroll');
    toast({
      title: 'View reset to defaults',
      duration: 5000,
      action: (
        <ToastAction
          altText="Undo reset view"
          onClick={() => {
            const prev = previousViewStateRef.current;
            if (prev) {
              setFilters(prev.filters);
              setShowInsights(prev.showInsights);
              setGroupBy(prev.groupBy as GroupByOption);
              // Restore column visibility directly from the saved snapshot.
              // Using restoreColumns() avoids a stale-closure bug: a toggle-loop
              // comparing against `isColVisible` would read the pre-reset
              // visibility (same as prev.colVisibility), so no column would
              // ever be toggled back.
              restoreColumns(prev.colVisibility);
              setItemCount(prev.showItemCount);
              setIsTopBarHidden(prev.isTopBarHidden);
              previousViewStateRef.current = null;
            }
          }}
        >
          Undo
        </ToastAction>
      ),
    });
  }, [dispatch, toast, filters, showInsights, groupBy, colVisibility, showItemCount, isTopBarHidden, handleResetVisibleDetails, restoreColumns, setItemCount]);

  const hasFetchedDeps = useRef(false);

  // [T122] Load tag stats (tags list + analytics summary merged)
  const loadTagStats = useCallback(async () => {
    setTagStatsLoading(true);
    try {
      const [tagsRes, summaryRes] = await Promise.all([
        api.get('/transactions/tags'),
        api.get('/transactions/analytics/tags-summary'),
      ]);
      const tagsList = (tagsRes.data as { success: boolean; data: string[] }).data ?? [];
      const summaryList = (summaryRes.data as { success: boolean; data: { tags: { tag: string; count: number; amount: number }[] } }).data?.tags ?? [];
      const mergedMap = new Map<string, { tag: string; count: number; amount: number }>();
      const upsertTag = (rawTag: string, count = 0, amount = 0) => {
        const tag = rawTag.trim();
        if (!tag) return;
        const key = tag.toLowerCase();
        const existing = mergedMap.get(key);
        if (existing) {
          existing.count += count;
          existing.amount += amount;
          return;
        }
        mergedMap.set(key, { tag, count, amount });
      };
      tagsList.forEach((tag) => upsertTag(tag));
      summaryList.forEach((summary) => upsertTag(summary.tag, summary.count, summary.amount));
      const merged = [...mergedMap.values()].sort((a, b) => a.tag.localeCompare(b.tag));
      setTagStats(merged);
    } catch {
      setTagStats([]);
    } finally {
      setTagStatsLoading(false);
    }
  }, []);

  // [T122] Rename a tag across all transactions
  const handleTagRename = async () => {
    if (!renamingTag || !renameValue.trim() || renameValue.trim() === renamingTag) return;
    try {
      await api.put('/transactions/tags/rename', { oldTag: renamingTag, newTag: renameValue.trim() });
      toast({ title: 'Tag renamed', description: `"${renamingTag}" renamed to "${renameValue.trim()}".` });
      setRenamingTag(null);
      loadTagStats();
    } catch (err: unknown) {
      toast({ title: 'Rename failed', description: String(err), variant: 'destructive' });
    }
  };

  // [T122] Delete a tag from all transactions
  const handleTagDelete = async () => {
    if (!tagToDelete) return;
    try {
      await api.delete(`/transactions/tags/${encodeURIComponent(tagToDelete)}`);
      toast({ title: 'Tag deleted', description: `"${tagToDelete}" removed from all transactions.` });
      setTagToDelete(null);
      loadTagStats();
    } catch (err: unknown) {
      toast({ title: 'Delete failed', description: String(err), variant: 'destructive' });
    }
  };

  // [T123] Create a new standalone tag
  const handleCreateTag = async () => {
    const trimmed = createTagValue.trim();
    if (!trimmed) return;
    try {
      await api.post('/transactions/tags', { tag: trimmed });
      toast({ title: 'Tag created', description: `"${trimmed}" added.` });
      setCreatingTag(false);
      setCreateTagValue('');
      loadTagStats();
    } catch (err: unknown) {
      toast({ title: 'Create failed', description: String(err), variant: 'destructive' });
    }
  };

  // Fetch categories, accounts, and extensions once
  useEffect(() => {
    if (hasFetchedDeps.current) return;
    hasFetchedDeps.current = true;
    dispatch(fetchCategories());
    dispatch(fetchAccounts());
    dispatch(fetchExtensions(undefined));
    loadTagStats();
  }, [dispatch, loadTagStats]);

// [T270-PY] Day-of-week drill-through is contractually ready on the FE (URL round-trips via
// filters.ts dayOfWeek field, ?dow= CSV parse) but the BE transactions listing endpoint
// does NOT yet filter by Mongo $dayOfWeek — see T270 contract preflight BLOCKER-1
// (`transactionHandler.ts:80-103`). Until BE ships matching support, the forwarded `dow`
// param is harmless to send but the API will ignore it, causing the chip to misrepresent the
// returned list. Flip to true once the BE listing endpoint accepts ?dow=.
const T270_DOW_BE_READY = false;

  // Fetch transactions when page or filters change
  const loadTransactions = useCallback(() => {
    const dateRange = effectiveCalendarDateRange ?? activeDateRange;
    if (import.meta.env.DEV) {
      console.log('[loadTransactions]', { period: filters.period, timezone, dateRange });
    }
    dispatch(
      fetchTransactions({
        page: pagination.currentPage,
        limit: pagination.pageSize,
        ...(filters.type && { type: filters.type }),
        ...(filters.accountIds?.[0] && { accountId: filters.accountIds[0] }),
        ...(filters.categoryIds?.[0] && { categoryId: filters.categoryIds[0] }),
        // [T270-PY] Day-of-week drill-through — gated behind T270_DOW_BE_READY = false until
        // the BE listing endpoint accepts ?dow= filtering. When flipped to true, the
        // canonical CSV form matches filtersToApiParams in @/lib/filters.ts.
        ...(T270_DOW_BE_READY && filters.dayOfWeek?.length
          ? { dow: filters.dayOfWeek.join(',') }
          : undefined),
        ...dateRange,
        ...(filters.search && { search: filters.search }),
        ...(filters.amountMin != null && { amountMin: filters.amountMin }),
        ...(filters.amountMax != null && { amountMax: filters.amountMax }),
        ...(filters.tags?.length && { tags: filters.tags.join(',') }),
        ...(filters.isRecurring && { isRecurring: 'true' }),
        ...(filters.status && { status: filters.status }),
        ...(subscriptionIdFromUrl && { subscriptionId: subscriptionIdFromUrl }),
        ...(timezone && timezone !== 'UTC' && { timezone }),
      })
    );
  }, [activeDateRange, dispatch, effectiveCalendarDateRange, pagination.currentPage, pagination.pageSize, filters, timezone, subscriptionIdFromUrl, T270_DOW_BE_READY]);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  // [T114-F5-k] Success handler for batch operations
  const handleBatchSuccess = useCallback(() => {
    clearSelection();
    loadTransactions();
  }, [clearSelection, loadTransactions]);

  // ---- Category handlers [T45P0] ----
  const handleCategorySubmit = async (values: CategoryFormValues) => {
    setIsSubmittingCategory(true);
    const payload = {
      name: values.name,
      type: values.type,
      color: values.color,
      description: values.description || undefined,
      emoji: values.emoji || undefined,
    } as Partial<Category>;

    try {
      if (selectedCategory) {
        await dispatch(updateCategory({ id: selectedCategory.id, data: payload })).unwrap();
        toast({ title: 'Category updated', description: `${values.name} has been saved.` });
      } else {
        await dispatch(createCategory(payload)).unwrap();
        toast({ title: 'Category created', description: `${values.name} added.` });
      }
      setCategoryDialogOpen(false);
      setSelectedCategory(null);
    } catch (error: unknown) {
      toast({ title: 'Unable to save category', description: (error instanceof Error ? error.message : '') || 'Please try again', variant: 'destructive' });
    } finally {
      setIsSubmittingCategory(false);
    }
  };

  const handleDeleteCategory = async () => {
    if (!categoryToDelete) return;
    try {
      await dispatch(deleteCategory(categoryToDelete.id)).unwrap();
      toast({ title: 'Category deleted', description: `${categoryToDelete.name} removed.` });
    } catch (error: unknown) {
      toast({ title: 'Unable to delete category', description: (error instanceof Error ? error.message : '') || 'Please try again', variant: 'destructive' });
    } finally {
      setCategoryToDelete(null);
    }
  };

  const handleCreate = async (data: TransactionFormData) => {
    const tags = data.tags?.map((tag) => tag.trim()).filter(Boolean) ?? [];

    // [T68P1-c] Offline mode: queue for later sync
    if (!isOnline && data.type !== 'transfer') {
      const optimisticId = crypto.randomUUID();
      const now = new Date().toISOString();
      const optimistic: Transaction = {
        id: optimisticId,
        userId: '',
        accountId: data.accountId,
        type: data.type,
        amount: data.amount,
        discountAmount: data.discountAmount ?? undefined,
        description: data.description,
        date: data.date.toISOString(),
        categoryId: data.categoryId && data.categoryId !== 'none' ? data.categoryId : undefined,
        notes: data.notes || undefined,
        isRecurring: data.isRecurring,
        emoji: data.emoji || undefined,
        tags,
        isPending: true,
        createdAt: now,
        updatedAt: now,
      };
      dispatch(addOptimisticTransaction(optimistic));
      addToQueue({
        resource: 'transactions',
        action: 'CREATE',
        payload: {
          type: data.type,
          amount: data.amount,
          discountAmount: data.discountAmount ?? undefined,
          description: data.description,
          date: data.date.toISOString(),
          accountId: data.accountId,
          categoryId: data.categoryId && data.categoryId !== 'none' ? data.categoryId : undefined,
          notes: data.notes || undefined,
          isRecurring: data.isRecurring,
          emoji: data.emoji || undefined,
          tags,
        },
        optimisticId,
        maxRetries: 3,
      });
      toast({ title: 'Saved offline', description: 'Transaction will sync when you reconnect.' });
      modalStack.clear();
      return;
    }

    setFormLoading(true);
    try {
      if (data.type === 'transfer') {
        await dispatch(
          transferBetweenAccounts({
            fromAccountId: data.accountId,
            toAccountId: data.toAccountId!,
            amount: data.amount,
            description: data.description,
            date: data.date.toISOString(),
          })
        ).unwrap();
      } else {
        const createdTransaction = await dispatch(
          createTransaction({
            type: data.type,
            amount: data.amount,
            discountAmount: data.discountAmount ?? undefined,
            description: data.description,
            date: data.date.toISOString(),
            accountId: data.accountId,
            categoryId: data.categoryId && data.categoryId !== 'none' ? data.categoryId : undefined,
            currency: data.currency || undefined,
            notes: data.notes || undefined,
            isRecurring: data.isRecurring,
            emoji: data.emoji || undefined,
            tags,
            // T80P1: Include items if present
            items: data.items && data.items.length > 0
              ? data.items.map((item) => ({
                  description: item.description,
                  amount: item.amount,
                  discountAmount: item.discountAmount ?? undefined,
                  quantity: item.quantity ?? 1,
                  categoryId: item.categoryId && item.categoryId !== 'none' ? item.categoryId : undefined,
                  notes: item.notes || undefined,
                  emoji: item.emoji || undefined,
                }))
              : undefined,
          })
        ).unwrap();

        if (data.pendingReceiptId) {
          try {
            await dispatch(linkReceipt({ id: data.pendingReceiptId, transactionId: createdTransaction.id })).unwrap();
          } catch (receiptErr: unknown) {
            toast({
              title: 'Receipt not linked',
              description: String(receiptErr),
              variant: 'destructive',
            });
          }
        }
      }
      toast({ title: 'Transaction created', description: 'Your transaction has been added.' });
      modalStack.clear();
      loadTransactions();
    } catch (err: unknown) {
      toast({ title: 'Error', description: String(err), variant: 'destructive' });
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdate = async (data: TransactionFormData) => {
    if (!editingTransaction) return;
    const tags = data.tags?.map((tag) => tag.trim()).filter(Boolean) ?? [];
    setFormLoading(true);
    try {
      await dispatch(
        updateTransaction({
          id: editingTransaction.id,
          data: {
            type: data.type,
            amount: data.amount,
            discountAmount: data.discountAmount ?? undefined,
            description: data.description,
            date: data.date.toISOString(),
            accountId: data.accountId,
            categoryId: data.categoryId && data.categoryId !== 'none' ? data.categoryId : undefined,
            currency: data.currency || undefined,
            notes: data.notes || undefined,
            emoji: data.emoji || undefined,
            tags,
            // T80P1: Include items if present, or empty array to remove
            items: data.items && data.items.length > 0
              ? data.items.map((item) => ({
                  description: item.description,
                  amount: item.amount,
                  discountAmount: item.discountAmount ?? undefined,
                  quantity: item.quantity ?? 1,
                  categoryId: item.categoryId && item.categoryId !== 'none' ? item.categoryId : undefined,
                  notes: item.notes || undefined,
                  emoji: item.emoji || undefined,
                }))
              : [],
          },
        })
      ).unwrap();
      toast({ title: 'Transaction updated' });
      modalStack.clear();
      loadTransactions();
    } catch (err: unknown) {
      toast({ title: 'Error', description: String(err), variant: 'destructive' });
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingId) return;
    try {
      await dispatch(deleteTransaction(deletingId)).unwrap();
      toast({ title: 'Transaction deleted' });
      setDeletingId(null);
      loadTransactions();
    } catch (err: unknown) {
      toast({ title: 'Error', description: String(err), variant: 'destructive' });
    }
  };

  const totalPages = Math.ceil(pagination.totalItems / pagination.pageSize) || 1;

  // [T60P0] Group transactions by selected criteria
  const groupedTransactions = useMemo(() => {
    if (groupBy === 'none') return null;

    const groups: Record<string, Transaction[]> = {};
    for (const txn of transactions) {
      let key: string;
      switch (groupBy) {
        case 'date':
          key = formatInUserTimezone(txn.date, 'date-weekday');
          break;
        case 'type':
          key = txn.type.charAt(0).toUpperCase() + txn.type.slice(1);
          break;
        case 'category': {
          const cat = categories.find((c) => c.id === txn.categoryId);
          key = cat ? (cat.emoji ? `${cat.emoji} ${cat.name}` : cat.name) : 'Uncategorized';
          break;
        }
        case 'account': {
          const acc = accounts.find((a) => a.id === txn.accountId);
          key = acc?.name ?? 'Unknown Account';
          break;
        }
        default:
          key = 'Other';
      }
      if (!groups[key]) groups[key] = [];
      groups[key].push(txn);
    }
    return groups;
  }, [transactions, groupBy, categories, accounts, formatInUserTimezone]);

  // [T60P0] Handle page size change
  const handlePageSizeChange = (size: string) => {
    dispatch(setPageSize(Number(size)));
  };

  // [T81P1-c] Duplicate a transaction
  const handleDuplicate = useCallback(async (txn: Transaction) => {
    try {
      const result = await dispatch(duplicateTransaction(txn.id)).unwrap();
      modalStack.push({ kind: 'edit', transactionId: result.id });
      toast({ title: 'Transaction duplicated', description: 'Edit and save to confirm.' });
    } catch (err) {
      toast({ title: 'Duplicate failed', description: String(err), variant: 'destructive' });
    }
  }, [dispatch, toast, modalStack]);

  // [T60P0] Handle detail sheet open — push a detail entry onto the stack.
  const handleViewDetail = (txn: Transaction) => {
    modalStack.push({ kind: 'detail', transactionId: txn.id });
  };

  const calendarDayLabel = calendarDayFilter
    ? new Date(`${calendarDayFilter}T00:00:00`).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  return (
    <div className="space-y-3 md:space-y-4">
      <PageGuide pageKey="transactions" content={transactionsGuide} />

      {/* [Phase5] URL-based pill tabs: Transactions | Templates */}
      <Tabs value={activeTab} onValueChange={(val) => {
        const next = new URLSearchParams(searchParams);
        next.set('tab', val);
        setSearchParams(next, { replace: true });
      }}>
        <TabsList className="mb-3">
          <TabsTrigger value="transactions" className="min-h-[44px]" data-testid="tab-transactions">
            Transactions
          </TabsTrigger>
          <TabsTrigger value="templates" className="min-h-[44px]" data-testid="tab-templates">
            <FileText className="mr-1.5 h-4 w-4" />
            Templates
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {activeTab === 'transactions' && (<>
      {/* [T115-F2] Unified Top Bar — merges header, filters, and batch actions */}
      <UnifiedTopBar
        totalCount={pagination.totalItems}
        filters={filters}
        accounts={accounts}
        categories={categories}
        allTags={allTags}
        tagStats={tagStats}
        onFiltersChange={(f) => { setFilters(f); dispatch(setPage(1)); }}
        onClear={() => { setFilters({ period: '30d' }); dispatch(setPage(1)); }}
        onRefresh={loadTransactions}
        onExport={() => {/* handled by exportTrigger */}}
        onScanReceipt={() => setShowScanner(true)}
        onAddTransaction={() => { modalStack.push({ kind: 'add' }); }}
        showInsights={showInsights}
        groupBy={groupBy}
        selectionMode={selectionMode}
        onToggleSelectionMode={toggleSelectionMode}
        onToggleInsights={() => setShowInsights(!showInsights)}
        onGroupByChange={(val) => setGroupBy(val as GroupByOption)}
        onResetView={handleResetView}
        visibleDetails={colVisibility}
        onToggleVisibleDetail={toggleColumn}
        onResetVisibleDetails={handleResetVisibleDetails}
        showItemCount={showItemCount}
        onShowItemCountChange={setItemCount}
        selectedCount={selectedIds.size}
        onEditCategory={() => setShowBatchUpdate(true)}
        onAddRemoveTags={() => setShowBatchTag(true)}
        onDelete={() => setShowBatchDelete(true)}
        onApplyRule={() => setShowBatchApplyRule(true)}
        onClearSelection={clearSelection}
        transactionDateBounds={transactionDateBounds}
        exportTrigger={
          <TransactionExportDialog
            initialFilters={filters}
            transactions={transactions}
            accounts={accounts}
            categories={categories}
            trigger={
              <Button
                variant="ghost"
                size="sm"
                className="h-8 min-h-[44px] md:min-h-0 gap-1.5 text-xs shrink-0"
                data-testid="export-btn"
              >
                <Download className="h-3.5 w-3.5" />
                Export
              </Button>
            }
          />
        }
        // [T-FLOATING-BAR-TOGGLE-PY] Page-owned full-bar visibility + toggle
        isTopBarHidden={isTopBarHidden}
        onToggleTopBarHidden={() => setIsTopBarHidden((prev) => !prev)}
      />
      {/* [T114-F5-f] Mobile-only batch bottom bar */}
      <BatchActionsToolbar
        selectedCount={selectedIds.size}
        onEditCategory={() => setShowBatchUpdate(true)}
        onAddRemoveTags={() => setShowBatchTag(true)}
        onDelete={() => setShowBatchDelete(true)}
        onApplyRule={() => setShowBatchApplyRule(true)}
        onClearSelection={clearSelection}
      />

      {/* [T61P0] Transaction Insights — full variant, period driven by filters */}
      <div className={cn(
        'grid transition-all duration-300 ease-in-out',
        showInsights
          ? 'grid-rows-[1fr] opacity-100'
          : 'grid-rows-[0fr] opacity-0',
      )}>
        <div className="overflow-hidden min-h-0">
          <TransactionInsightsCard
            variant="full"
            transactions={transactions}
            accounts={accounts}
            categories={categories}
            filters={filters}
            onCalendarDayFilter={handleCalendarDayFilter}
          />
        </div>
      </div>



      {/* [T128] Subscription filter banner — shown when navigating from Subscriptions page */}
      {subscriptionIdFromUrl && (
        <div className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30 px-3 py-2 text-sm text-blue-700 dark:text-blue-300 mb-3">
          <CreditCard className="h-4 w-4 shrink-0" />
          <span>Showing transactions linked to this subscription.</span>
          <Button variant="link" size="sm" className="p-0 h-auto text-blue-700 dark:text-blue-300 ml-auto" onClick={() => navigate('/transactions', { replace: true })}>
            Clear filter
          </Button>
        </div>
      )}

      {/* Transaction List */}
      <Card className="overflow-hidden transition-all duration-200 hover:shadow-md hover:border-primary/20" data-testid="transactions-history-card">
        <CardHeader className="flex flex-col items-stretch gap-3 pb-4">
          <div className="flex min-w-0 items-center gap-4">
            <div className="p-2 bg-primary/10 rounded-full">
              <ArrowLeftRight className="h-5 w-5 text-primary" />
            </div>
            <CardTitle>Transaction History</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-hidden">
          {calendarDayFilter && calendarDayLabel && (
            <div
              className="relative z-0 mb-3 flex min-h-[52px] w-full items-center justify-between gap-2 rounded-md border border-l-4 border-primary/40 border-l-primary bg-muted/70 px-3 py-1.5 text-xs text-foreground shadow-sm"
              data-testid="calendar-day-filter-chip"
              style={{ scrollMarginTop: '18rem' }}
            >
              <span className="break-words font-medium" data-testid="calendar-day-filter-header">
                Transactions for {calendarDayLabel} · {pagination.totalItems} {pagination.totalItems === 1 ? 'transaction' : 'transactions'}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-11 w-11 min-h-[44px] min-w-[44px] shrink-0 border-border bg-background p-0 text-foreground shadow-sm hover:bg-muted"
                aria-label={`Clear calendar filter for ${calendarDayLabel}`}
                data-testid="calendar-day-filter-clear"
                onClick={() => clearCalendarDayFilter()}
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
          )}
          {isLoading && transactions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center" data-testid="transactions-loading-state">
              <ArrowLeftRight className="h-10 w-10 text-muted-foreground/50 motion-safe:animate-pulse" />
              <p className="mt-3 text-sm text-muted-foreground">Loading transactions...</p>
            </div>
          ) : transactionsError && transactions.length === 0 ? (
            <Alert variant="destructive" className="my-4" data-testid="transactions-error">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Failed to load transactions</AlertTitle>
              <AlertDescription className="flex flex-col gap-2">
                <span>{transactionsError}</span>
                <Button variant="outline" size="sm" className="w-fit" onClick={loadTransactions}>
                  <RotateCcw className="mr-2 h-3.5 w-3.5" />
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          ) : transactions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <ArrowLeftRight className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-semibold">No transactions yet</h3>
              <p className="text-sm text-muted-foreground mt-1 mb-4">
                Start tracking your finances by adding your first transaction.
              </p>
              <Button onClick={() => modalStack.push({ kind: 'add' })} data-testid="add-transaction-empty-btn">
                <Plus className="mr-2 h-4 w-4" />
                Add Transaction
              </Button>
            </div>
          ) : (
            <>
              {isLoading && transactions.length > 0 && (
                <div
                  className="mb-3 inline-flex w-full items-center gap-2 rounded-md border border-border/50 bg-muted/20 px-3 py-2 text-xs text-muted-foreground"
                  data-testid="transactions-refreshing-indicator"
                  role="status"
                  aria-live="polite"
                >
                  <RotateCcw className="h-3.5 w-3.5 shrink-0 animate-spin" />
                  <span className="break-words">Refreshing transactions...</span>
                </div>
              )}

              {groupBy === 'none' ? (
                <div className="divide-y divide-border/40 overflow-x-hidden" data-testid="transactions-adaptive-list">
                  {transactions.map((txn) => (
                    <AdaptiveTransactionRow
                      key={txn.id}
                      txn={txn}
                      categories={categories}
                      accounts={accounts}
                      fmt={fmt}
                      formatDate={formatInUserTimezone}
                      isColVisible={isColVisible}
                      showLineItems={showLineItems}
                      showItemCount={showItemCount}
                      onView={handleViewDetail}
                      onDuplicate={handleDuplicate}
                      onDelete={setDeletingId}
                      onOpenItemsSheet={isDesktopInlineItems ? undefined : handleOpenMobileItems}
                      selectionMode={selectionMode}
                      onLongPressSelect={handleLongPressSelect}
                      isSelected={selectedIds.has(txn.id)}
                      onToggleSelect={toggleSelect}
                    />
                  ))}
                </div>
              ) : (
                <div className="space-y-2 overflow-x-hidden" data-testid="transactions-grouped-adaptive-list">
                  {groupedTransactions && Object.entries(groupedTransactions).map(([group, txns]) => {
                    const groupIds = txns.map((t) => t.id);
                    const groupAllSelected = groupIds.length > 0 && groupIds.every((id) => selectedIds.has(id));
                    const groupSomeSelected = !groupAllSelected && groupIds.some((id) => selectedIds.has(id));
                    const groupKey = toStableGroupKey(group);
                    const groupNet = getGroupSignedNetTotal(txns);
                    const isNegativeGroupNet = groupNet < 0;

                    return (
                      <section key={group} className="overflow-hidden rounded-lg border border-border/50 bg-card/50">
                        <div
                          className="flex min-h-[2.75rem] items-center justify-between gap-2 border-b border-border/40 bg-muted/20 px-2.5 py-1.5"
                          data-testid={`txn-group-row-${groupKey}`}
                        >
                          <div
                            className="flex min-w-0 flex-1 items-center gap-2 self-center"
                            data-testid={`group-label-cluster-${groupKey}`}
                          >
                            {selectionMode && (
                              <Checkbox
                                checked={groupAllSelected ? true : groupSomeSelected ? 'indeterminate' : false}
                                onCheckedChange={() => toggleSelectGroup(groupIds)}
                                aria-label={`Select all in ${group}`}
                                className="shrink-0"
                                data-testid={`group-select-${groupKey}`}
                              />
                            )}
                            <h4 className="min-w-0 text-sm font-semibold leading-snug text-muted-foreground break-words">{group}</h4>
                          </div>
                          <div
                            className="flex shrink-0 items-center gap-2 self-center"
                            data-testid={`group-summary-cluster-${groupKey}`}
                          >
                            <p
                              className={cn(
                                'shrink-0 text-sm font-semibold leading-tight tabular-nums whitespace-nowrap',
                                isNegativeGroupNet ? 'text-red-600' : 'text-green-600',
                              )}
                              data-testid={`group-net-${groupKey}`}
                            >
                              Net {isNegativeGroupNet ? '-' : ''}{fmt(Math.abs(groupNet))}
                            </p>
                            <Badge variant="outline" className="shrink-0 inline-flex h-6 items-center rounded-md px-2 text-[11px] tabular-nums whitespace-nowrap" data-testid={`group-count-${groupKey}`}>
                              {txns.length} txn{txns.length !== 1 ? 's' : ''}
                            </Badge>
                          </div>
                        </div>

                        <div className="divide-y divide-border/40 overflow-x-hidden">
                          {txns.map((txn) => (
                            <AdaptiveTransactionRow
                              key={txn.id}
                              txn={txn}
                              categories={categories}
                              accounts={accounts}
                              fmt={fmt}
                              formatDate={formatInUserTimezone}
                              isColVisible={isColVisible}
                              showLineItems={showLineItems}
                              showItemCount={showItemCount}
                              onView={handleViewDetail}
                              onDuplicate={handleDuplicate}
                              onDelete={setDeletingId}
                              onOpenItemsSheet={isDesktopInlineItems ? undefined : handleOpenMobileItems}
                              selectionMode={selectionMode}
                              onLongPressSelect={handleLongPressSelect}
                              isSelected={selectedIds.has(txn.id)}
                              onToggleSelect={toggleSelect}
                            />
                          ))}
                        </div>
                      </section>
                    );
                  })}
                </div>
              )}

              {/* Pagination — [T60P0] enhanced with page size selector */}
              <div className="flex flex-col gap-3 sm:flex-row items-center justify-between pt-4" data-testid="transactions-pagination">
                <div className="flex items-center gap-3">
                  <p className="text-sm text-muted-foreground">
                    Page {pagination.currentPage} of {totalPages}
                  </p>
                  <PageSizeSelector
                    value={pagination.pageSize}
                    onChange={(size) => handlePageSizeChange(String(size))}
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="min-h-[44px] min-w-[44px]"
                    data-testid="transactions-pagination-prev"
                    disabled={pagination.currentPage <= 1}
                    onClick={() => dispatch(setPage(pagination.currentPage - 1))}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="min-h-[44px] min-w-[44px]"
                    data-testid="transactions-pagination-next"
                    disabled={pagination.currentPage >= totalPages}
                    onClick={() => dispatch(setPage(pagination.currentPage + 1))}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* [Phase E] Top-of-stack is the only transaction modal rendered.
          - `kind:'add'` opens the create form with optional scanner/category initialValues.
          - `kind:'edit'` opens the update form; `stack` + `onBack` enable the Back-to-Detail button. */}
      {topEntry?.kind === 'add' && (
        <ErrorBoundary>
          <TransactionForm
            open={true}
            onOpenChange={(open) => { if (!open) modalStack.pop(); }}
            onSubmit={handleCreate}
            initialValues={topEntry.initialValues}
            accounts={accounts}
            categories={categories}
            isLoading={formLoading}
            stack={modalStack.stack}
          />
        </ErrorBoundary>
      )}

      {topEntry?.kind === 'edit' && (
        <ErrorBoundary>
          <TransactionForm
            open={true}
            onOpenChange={(open) => { if (!open) modalStack.pop(); }}
            onSubmit={handleUpdate}
            transaction={editingTransaction}
            accounts={accounts}
            categories={categories}
            isLoading={formLoading}
            stack={modalStack.stack}
            onBack={() => modalStack.pop()}
          />
        </ErrorBoundary>
      )}

      {/* Receipt Scanner Dialog */}
      <ErrorBoundary>
        <ReceiptScannerDialog
          open={showScanner}
          onOpenChange={setShowScanner}
          categories={categories}
          onScanned={handleScanned}
        />
      </ErrorBoundary>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingId} onOpenChange={(open) => !open && setDeletingId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Transaction</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this transaction? This action cannot be undone
              and the account balance will be adjusted accordingly.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Categories Section [T45P0 — moved from Accounts page] ── */}
      <Separator className="my-4 md:my-6" />

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold">Categories</h3>
            <p className="text-sm text-muted-foreground">Organize transactions by income and expense buckets.</p>
          </div>
          <Button size="sm" onClick={() => setCategoryDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> Add Category
          </Button>
        </div>

        {/* [T84P2-c] Category search & type filter */}
        {categories.length > 0 && (
          <div className="flex flex-col gap-2 overflow-hidden sm:flex-row sm:items-center sm:overflow-visible" data-testid="category-filters">
            <div className="relative min-w-0 w-full sm:flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search categories..."
                data-testid="category-search-input"
                value={categorySearchQuery}
                onChange={(e) => setCategorySearchQuery(e.target.value)}
                className="pl-9 min-w-0"
              />
            </div>
            <Tabs
              value={categoryTypeFilter}
              onValueChange={(v) => setCategoryTypeFilter(v as 'all' | 'income' | 'expense')}
              className="w-full min-w-0 sm:w-auto sm:shrink-0"
            >
              <TabsList data-testid="category-type-filter" className="grid w-full min-w-0 grid-cols-3 sm:w-auto">
                <TabsTrigger value="all" className="min-h-[44px] min-w-0 px-2 text-xs sm:min-h-[36px] sm:text-sm">All</TabsTrigger>
                <TabsTrigger value="income" className="min-h-[44px] min-w-0 px-2 text-xs sm:min-h-[36px] sm:text-sm">Income</TabsTrigger>
                <TabsTrigger value="expense" className="min-h-[44px] min-w-0 px-2 text-xs sm:min-h-[36px] sm:text-sm">Expense</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        )}

        {/* Category cards */}
        {categoriesLoading && categories.length === 0 ? (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {[...Array(3)].map((_, idx) => (
              <Card key={idx}>
                <CardContent className="space-y-2 p-4">
                  <Skeleton className="h-5 w-32" />
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-40" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : categories.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-8 text-center">
            <Badge variant="secondary">Categories</Badge>
            <p className="mt-3 text-sm text-muted-foreground">No categories yet. Add one to organize transactions.</p>
            <Button className="mt-4" onClick={() => setCategoryDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" /> Add Category
            </Button>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="categories-grid">
            {filteredCategories.map((category) => (
              <Card
                key={category.id}
                data-testid={`category-card-${category.name?.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and')}`}
                className="border transition-all duration-200 hover:shadow-md hover:border-primary/20 cursor-pointer"
                role="button"
                tabIndex={0}
                onClick={() => { setDetailCategoryId(category.id); setCategoryDetailOpen(true); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDetailCategoryId(category.id); setCategoryDetailOpen(true); } }}
              >
                <CardContent className="p-0">
                  {/* Color accent bar */}
                  <div
                    className="h-1.5 w-full rounded-t-lg"
                    style={{ backgroundColor: category.color || '#6b7280' }}
                  />
                  <div className="p-4 flex items-center gap-3">
                    {/* Icon / Emoji */}
                    {category.emoji ? (
                      <span
                        className="h-10 w-10 rounded-lg border flex items-center justify-center text-lg flex-shrink-0"
                        style={{ backgroundColor: category.color ? `${category.color}15` : '#f3f4f6' }}
                        aria-label={category.name}
                      >
                        {category.emoji}
                      </span>
                    ) : (
                      <span
                        className="h-10 w-10 rounded-lg border flex-shrink-0"
                        style={{ backgroundColor: category.color || '#e5e7eb' }}
                        aria-label={category.name}
                      />
                    )}
                    {/* Name + description */}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold leading-tight break-words min-w-0">{category.name}</p>
                      {category.description && (
                        <p className="text-xs text-muted-foreground mt-0.5 break-words">{category.description}</p>
                      )}
                    </div>
                    {/* Actions */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-[44px] w-[44px] flex-shrink-0"
                          aria-label={`Manage ${category.name}`}
                          data-testid={`manage-category-${category.name.toLowerCase().replace(/\s+/g, '-')}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" collisionPadding={8}>
                        <DropdownMenuItem
                          data-testid={`category-edit-${category.name.toLowerCase().replace(/\s+/g, '-')}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedCategory(category);
                            setCategoryDialogOpen(true);
                          }}
                        >
                          <Pencil className="mr-2 h-4 w-4" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          data-testid={`category-delete-${category.name.toLowerCase().replace(/\s+/g, '-')}`}
                          className="text-destructive"
                          onClick={(e) => { e.stopPropagation(); setCategoryToDelete(category); }}
                        >
                          <Trash2 className="mr-2 h-4 w-4" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* ── Tags Section [T122] ── */}
      <Separator className="my-4 md:my-6" />

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold">Tags</h3>
            <p className="text-sm text-muted-foreground">Organize transactions with custom labels.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => { setCreateTagValue(''); setCreatingTag(true); }} data-testid="add-tag-btn">
              <Plus className="mr-2 h-4 w-4" /> Add Tag
            </Button>
            <IconButton
              tooltip="Refresh tags"
              variant="ghost"
              size="sm"
              onClick={loadTagStats}
            >
              <RotateCcw className="h-4 w-4" />
            </IconButton>
          </div>
        </div>

        {/* Tag Insights bar chart (top 5 by transaction count) */}
        {tagStats.length > 0 && (() => {
          const top5 = [...tagStats].sort((a, b) => b.count - a.count).slice(0, 5);
          const maxCount = top5[0]?.count ?? 1;
          return (
            <Card className="transition-all duration-200 hover:shadow-md hover:border-primary/20">
              <CardHeader className="flex flex-row items-center gap-3 pb-3 pt-4 px-4">
                <div className="p-1.5 bg-primary/10 rounded-full"><BarChart2 className="h-4 w-4 text-primary" /></div>
                <CardTitle className="text-base">Tag Insights</CardTitle>
                <Badge variant="secondary" className="ml-auto text-xs">{tagStats.length} tags</Badge>
              </CardHeader>
              <CardContent className="px-4 pb-4 space-y-2.5">
                {top5.map((ts) => (
                  <div key={ts.tag} className="space-y-1">
                    <div className="flex items-center justify-between gap-2">
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 text-xs font-medium rounded-full border px-2.5 py-1 cursor-pointer transition-colors min-h-[28px] border-border/60 bg-muted/40 hover:bg-primary/10 hover:border-primary/40"
                        onClick={() => setTagSearchQuery(ts.tag)}
                      >
                        <Hash className="h-2.5 w-2.5" />
                        {ts.tag}
                      </button>
                      <span className="text-[11px] tabular-nums text-muted-foreground font-medium">
                        {ts.count} txn · {fmt(ts.amount)}
                      </span>
                    </div>
                    <div className="h-1.5 w-full rounded-full bg-muted/60 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-primary/60 transition-all duration-300"
                        style={{ width: `${(ts.count / maxCount) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          );
        })()}

        {/* Tag search */}
        {tagStats.length > 0 && (
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search tags..."
              value={tagSearchQuery}
              onChange={(e) => setTagSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        )}

        {/* Tag cards */}
        {tagStatsLoading && tagStats.length === 0 ? (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {[...Array(3)].map((_, idx) => (
              <Card key={idx}>
                <CardContent className="space-y-2 p-4">
                  <Skeleton className="h-5 w-32" />
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-40" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : tagStats.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-8 text-center">
            <Hash className="h-10 w-10 text-muted-foreground/40 mb-3" />
            <p className="text-sm text-muted-foreground">No tags yet. Add tags to your transactions to see them here.</p>
          </div>
        ) : filteredTagStats.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">No tags match your search.</p>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {filteredTagStats.map(({ tag, count, amount }) => {
              const color = getTagColor(tag);
              return (
                <Card
                  key={tag}
                  className="border transition-all duration-200 hover:shadow-md hover:border-primary/20 cursor-pointer"
                  role="button"
                  tabIndex={0}
                  onClick={() => { setDetailTag(tag); setTagDetailOpen(true); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDetailTag(tag); setTagDetailOpen(true); } }}
                  data-testid={`tag-card-${tag}`}
                >
                  <CardContent className="p-0">
                    {/* Color accent bar */}
                    <div className="h-1.5 w-full rounded-t-lg" style={{ backgroundColor: color }} />
                    <div className="p-4 flex items-center gap-3">
                      {/* Hash icon with color accent */}
                      <span
                        className="h-10 w-10 rounded-lg border flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${color}20` }}
                      >
                        <Hash className="h-4 w-4" style={{ color }} />
                      </span>
                      {/* Tag name + stats */}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold leading-tight break-words">{tag}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {count} transaction{count !== 1 ? 's' : ''} · {fmt(amount)}
                        </p>
                      </div>
                      {/* Actions */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 flex-shrink-0"
                            aria-label={`Manage tag ${tag}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" collisionPadding={8}>
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              setDetailTag(tag);
                              setTagDetailOpen(true);
                            }}
                            data-testid={`tag-view-detail-${tag}`}
                          >
                            <BarChart2 className="mr-2 h-4 w-4" /> View detail
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              setFilters((prev) => ({ ...prev, tags: [tag] }));
                              dispatch(setPage(1));
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                          >
                            <Eye className="mr-2 h-4 w-4" /> Filter transactions
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              setRenamingTag(tag);
                              setRenameValue(tag);
                            }}
                          >
                            <Pencil className="mr-2 h-4 w-4" /> Rename
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={(e) => { e.stopPropagation(); setTagToDelete(tag); }}
                          >
                            <Trash2 className="mr-2 h-4 w-4" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Category Form Dialog */}
      <CategoryForm
        open={categoryDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedCategory(null);
          }
          setCategoryDialogOpen(open);
        }}
        onSubmit={handleCategorySubmit}
        category={selectedCategory}
        isSubmitting={isSubmittingCategory}
      />

      {/* Category Delete Confirmation */}
      <AlertDialog open={!!categoryToDelete} onOpenChange={(open) => !open && setCategoryToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete category</AlertDialogTitle>
            <AlertDialogDescription>
              Deleting a category may affect transaction grouping. Proceed?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteCategory} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Tag Rename Dialog [T122] */}
      <Dialog open={!!renamingTag} onOpenChange={(open) => !open && setRenamingTag(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rename tag</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Input
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              placeholder="New tag name"
              onKeyDown={(e) => { if (e.key === 'Enter') handleTagRename(); }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenamingTag(null)}>Cancel</Button>
            <Button
              onClick={handleTagRename}
              disabled={!renameValue.trim() || renameValue.trim() === renamingTag}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tag Delete Confirmation [T122] */}
      <AlertDialog open={!!tagToDelete} onOpenChange={(open) => !open && setTagToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete tag</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the tag &quot;{tagToDelete}&quot; from all transactions.
              {(() => {
                const count = tagStats.find((s) => s.tag === tagToDelete)?.count ?? 0;
                return count > 0 ? (
                  <span className="font-medium text-destructive"> {count} transaction{count !== 1 ? 's' : ''} will be affected.</span>
                ) : null;
              })()}
              {' '}This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleTagDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create Tag Dialog [T123] */}
      <Dialog
        open={creatingTag}
        onOpenChange={(open) => { if (!open) { setCreatingTag(false); setCreateTagValue(''); } }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create tag</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Input
              value={createTagValue}
              onChange={(e) => setCreateTagValue(e.target.value)}
              placeholder="Tag name"
              maxLength={50}
              onKeyDown={(e) => { if (e.key === 'Enter') handleCreateTag(); }}
              autoFocus
              data-testid="create-tag-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreatingTag(false); setCreateTagValue(''); }}>Cancel</Button>
            <Button onClick={handleCreateTag} disabled={!createTagValue.trim()} data-testid="create-tag-submit">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* [T60P0] Transaction Detail Sheet — rendered only when the top of the
          stack is a `detail` entry. Footer Edit closes the sheet first (handled
          inside TransactionDetailSheet) before invoking `onEdit`, so the page
          uses `replace` to swap the now-empty top with the new edit entry.
          Row shortcuts (amount/notes) call `onEditShortcut` WITHOUT closing the
          sheet, so the page captures scrollY, patches the detail entry's meta,
          and pushes an edit entry. Successful Add/Edit `clear()`s the stack
          (preserves the existing behavior of closing all transaction modals). */}
      {topEntry?.kind === 'detail' && (
        <TransactionDetailSheet
          transaction={liveDetailTransaction}
          stack={modalStack.stack}
          open={true}
          onOpenChange={(open) => { if (!open) modalStack.pop(); }}
          accounts={accounts}
          categories={categories}
          onEdit={(txn) => { captureScrollAndPushEdit(txn.id); }}
          onEditShortcut={handleDetailEditShortcut}
          onDelete={(id) => {
            modalStack.pop();
            setDeletingId(id);
          }}
          onDuplicate={handleDuplicate}
          onRefresh={loadTransactions}
        />
      )}

      {/* T146: Mobile receipt-style line-items bottom-sheet */}
      <MobileItemsSheet
        open={!isDesktopInlineItems && mobileItemsTransaction !== null}
        onOpenChange={(open) => {
          if (!open) setMobileItemsTransaction(null);
        }}
        items={mobileItemsTransaction?.items ?? []}
        transactionAmount={mobileItemsTransaction?.amount ?? 0}
        transactionDiscountTotal={mobileItemsTransaction?.discountAmount ?? 0}
        transactionDate={mobileItemsTransaction?.date ?? new Date()}
        transactionDescription={mobileItemsTransaction?.description ?? ''}
        transactionEmoji={mobileItemsTransaction?.emoji}
      />

      {/* [T84P2] Category Detail Sheet */}
      <CategoryDetailSheet
        categoryId={detailCategoryId}
        open={categoryDetailOpen}
        onOpenChange={setCategoryDetailOpen}
        onEdit={(cat) => { setSelectedCategory(cat); setCategoryDialogOpen(true); }}
        onFilterByCategory={(catId) => {
          setFilters((prev) => ({ ...prev, categoryIds: [catId] }));
          dispatch(setPage(1));
        }}
        onAddTransaction={(catId) => {
          modalStack.push({ kind: 'add', initialValues: { categoryId: catId } });
        }}
      />

      {/* [T123] Tag Detail Sheet */}
      <TagDetailSheet
        tag={detailTag}
        open={tagDetailOpen}
        onOpenChange={setTagDetailOpen}
        onRename={(t) => { setRenamingTag(t); setRenameValue(t); }}
        onFilterByTag={(t) => {
          setFilters((prev) => ({ ...prev, tags: [t] }));
          dispatch(setPage(1));
        }}
      />

      {/* [T114-F5] Batch Operation Dialogs */}
      <BatchUpdateDialog
        open={showBatchUpdate}
        onOpenChange={setShowBatchUpdate}
        selectedIds={Array.from(selectedIds)}
        categories={categories}
        onSuccess={handleBatchSuccess}
      />
      <BatchDeleteDialog
        open={showBatchDelete}
        onOpenChange={setShowBatchDelete}
        selectedIds={Array.from(selectedIds)}
        onSuccess={handleBatchSuccess}
      />
      <BatchApplyRuleDialog
        open={showBatchApplyRule}
        onOpenChange={setShowBatchApplyRule}
        selectedIds={Array.from(selectedIds)}
        onSuccess={handleBatchSuccess}
      />
      <BatchTagDialog
        open={showBatchTag}
        onOpenChange={setShowBatchTag}
        selectedIds={Array.from(selectedIds)}
        existingTags={allTags}
        onSuccess={handleBatchSuccess}
      />
      </>)}

      {activeTab === 'templates' && (
        <TemplatesSection />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// [Phase5] Templates Section — rendered when ?tab=templates
// ---------------------------------------------------------------------------

function TemplatesSection() {
  const dispatch = useAppDispatch();
  const { toast } = useToast();
  const { templates, isLoading } = useAppSelector((state) => state.templates);

  const [templateFormOpen, setTemplateFormOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<TransactionTemplate | null>(null);
  const [detailTemplate, setDetailTemplate] = useState<TransactionTemplate | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [quickCreateTemplate, setQuickCreateTemplate] = useState<TransactionTemplate | null>(null);
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);

  const hasFetched = useRef(false);
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    dispatch(fetchTemplates());
  }, [dispatch]);

  const handleEdit = (tpl: TransactionTemplate) => {
    setEditingTemplate(tpl);
    setTemplateFormOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteConfirmId) return;
    try {
      await dispatch(deleteTemplate(deleteConfirmId)).unwrap();
      toast({ title: 'Template deleted' });
      setDeleteConfirmId(null);
      setDetailOpen(false);
    } catch (err: unknown) {
      toast({ title: 'Delete failed', description: String(err), variant: 'destructive' });
    }
  };

  const handleQuickCreate = (tpl: TransactionTemplate) => {
    setQuickCreateTemplate(tpl);
    setQuickCreateOpen(true);
  };

  return (
    <div className="space-y-4" data-testid="templates-section">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-semibold">Templates</h3>
          <p className="text-sm text-muted-foreground">Reusable templates for quick transaction creation.</p>
        </div>
        <Button
          size="sm"
          className="min-h-[44px]"
          onClick={() => { setEditingTemplate(null); setTemplateFormOpen(true); }}
          data-testid="btn-add-template"
        >
          <Plus className="mr-2 h-4 w-4" />
          New Template
        </Button>
      </div>

      <TemplateList
        templates={templates}
        isLoading={isLoading}
        onEdit={handleEdit}
        onDelete={(id) => setDeleteConfirmId(id)}
        onQuickCreate={handleQuickCreate}
        onView={(tpl) => { setDetailTemplate(tpl); setDetailOpen(true); }}
      />

      <TemplateForm
        open={templateFormOpen}
        onOpenChange={(open) => {
          if (!open) setEditingTemplate(null);
          setTemplateFormOpen(open);
        }}
        template={editingTemplate}
        onSuccess={() => {
          setTemplateFormOpen(false);
          dispatch(fetchTemplates());
        }}
      />

      <TemplateDetailSheet
        template={detailTemplate}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        onEdit={(tpl) => {
          setDetailOpen(false);
          handleEdit(tpl);
        }}
        onDelete={(id) => {
          setDetailOpen(false);
          setDeleteConfirmId(id);
        }}
        onQuickCreate={(tpl) => {
          setDetailOpen(false);
          handleQuickCreate(tpl);
        }}
      />

      <QuickCreateCard
        template={quickCreateTemplate}
        open={quickCreateOpen}
        onOpenChange={setQuickCreateOpen}
        onSuccess={() => dispatch(fetchTemplates())}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirmId} onOpenChange={(open) => !open && setDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this template? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// [T219PY] Adaptive transaction row — list-first across mobile/tablet/desktop
// ---------------------------------------------------------------------------

function AdaptiveTransactionRow({
  txn,
  categories,
  accounts,
  fmt,
  formatDate,
  isColVisible,
  showLineItems,
  showItemCount,
  onView,
  onDuplicate,
  onDelete,
  onOpenItemsSheet,
  selectionMode,
  onLongPressSelect,
  isSelected,
  onToggleSelect,
}: {
  txn: Transaction;
  categories: Category[];
  accounts: { id: string; name: string; emoji?: string }[];
  fmt: (n: number) => string;
  formatDate: (date: string | Date, style?: 'short' | 'medium' | 'long' | 'date-only' | 'time-only') => string;
  isColVisible: (id: TransactionColumnId) => boolean;
  showLineItems?: boolean;
  showItemCount?: boolean;
  onView: (txn: Transaction) => void;
  onDuplicate?: (txn: Transaction) => void;
  onDelete: (id: string) => void;
  onOpenItemsSheet?: (txn: Transaction) => void;
  selectionMode?: boolean;
  onLongPressSelect?: (id: string) => void;
  isSelected?: boolean;
  onToggleSelect?: (id: string) => void;
}) {
  const config = typeConfig[txn.type];
  const Icon = config.icon;
  const amountBreakdown = getTransactionAmountBreakdown(txn);
  const itemizedDiscountTotal = (txn.items ?? []).reduce((sum, item) => {
    const rawDiscount = Number(item.discountAmount ?? 0);
    const safeDiscount = Number.isFinite(rawDiscount) ? rawDiscount : 0;
    return sum + Math.max(0, safeDiscount);
  }, 0);
  const effectiveDiscount = Math.max(0, amountBreakdown.discount + itemizedDiscountTotal);
  const showDiscountBreakdown = effectiveDiscount > 0;
  const isNegativeNet = amountBreakdown.signedNet < 0;
  const rawCat = txn.category as unknown;
  const matchedCat = categories.find((c) => c.id === txn.categoryId || c.id === (typeof rawCat === 'object' && rawCat !== null ? (rawCat as TransactionCategory)._id || (rawCat as TransactionCategory).id : undefined));
  const categoryName = matchedCat?.name || (typeof rawCat === 'object' && rawCat !== null ? (rawCat as TransactionCategory).name : txn.category) || '';
  const catEmoji = matchedCat?.emoji;
  const catColor = matchedCat?.color;
  const matchedAccount = accounts.find((a) => a.id === txn.accountId);

  const hasItemRows = !!(txn.items && txn.items.length > 0);
  const hasItemizedData = !!(txn.hasItems || hasItemRows);
  const showNotes = isColVisible('notes');
  const showTags = isColVisible('tags');

  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressFeedbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressStartRef = useRef<{ pointerId: number; x: number; y: number } | null>(null);
  const longPressTriggeredRef = useRef(false);
  const [showLongPressFeedback, setShowLongPressFeedback] = useState(false);
  const [desktopItemsExpanded, setDesktopItemsExpanded] = useState(false);

  const clearLongPress = useCallback(() => {
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = null;
    longPressStartRef.current = null;
  }, []);

  const triggerLongPressFeedback = useCallback((pointerType: string) => {
    const prefersReducedMotion = typeof window !== 'undefined'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const supportsVibrate = pointerType !== 'mouse'
      && typeof navigator !== 'undefined'
      && typeof navigator.vibrate === 'function';
    const didVibrate = supportsVibrate ? navigator.vibrate(12) : false;

    if (!didVibrate || prefersReducedMotion) {
      setShowLongPressFeedback(true);
      if (longPressFeedbackTimerRef.current) clearTimeout(longPressFeedbackTimerRef.current);
      longPressFeedbackTimerRef.current = setTimeout(() => {
        setShowLongPressFeedback(false);
        longPressFeedbackTimerRef.current = null;
      }, 260);
    }
  }, []);

  useEffect(() => {
    return () => {
      clearLongPress();
      if (longPressFeedbackTimerRef.current) clearTimeout(longPressFeedbackTimerRef.current);
    };
  }, [clearLongPress]);

  const isInteractiveLongPressTarget = (
    target: EventTarget | null,
    currentTarget: EventTarget | null,
  ): boolean => {
    if (!(target instanceof HTMLElement)) return false;
    const interactiveAncestor = target.closest(
      'button, a, input, textarea, select, [role="button"], [role="menu"], [role="menuitem"], [role="link"], [role="switch"], [role="tab"], [role="checkbox"], [data-longpress-ignore="true"]',
    );
    if (!interactiveAncestor) return false;

    // The row itself uses role="button" for keyboard semantics; only nested controls should block long-press.
    if (currentTarget instanceof HTMLElement && interactiveAncestor === currentTarget) return false;

    return true;
  };

  const handleCardClick = () => {
    if (longPressTriggeredRef.current) {
      longPressTriggeredRef.current = false;
      return;
    }
    if (selectionMode) {
      onToggleSelect?.(txn.id);
      return;
    }
    onView(txn);
  };

  const handleCardKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    if (selectionMode) {
      onToggleSelect?.(txn.id);
      return;
    }
    onView(txn);
  };

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (selectionMode || event.button !== 0) return;
    if (isInteractiveLongPressTarget(event.target, event.currentTarget)) return;

    longPressTriggeredRef.current = false;
    longPressStartRef.current = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
    };

    const pointerType = event.pointerType;
    longPressTimerRef.current = setTimeout(() => {
      longPressTriggeredRef.current = true;
      triggerLongPressFeedback(pointerType);
      onLongPressSelect?.(txn.id);
      clearLongPress();
    }, 420);
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const start = longPressStartRef.current;
    if (!start || start.pointerId !== event.pointerId) return;
    const movement = Math.hypot(event.clientX - start.x, event.clientY - start.y);
    if (movement > 10) clearLongPress();
  };

  const handlePointerEnd = (event: ReactPointerEvent<HTMLDivElement>) => {
    const start = longPressStartRef.current;
    if (!start || start.pointerId !== event.pointerId) return;
    clearLongPress();
  };

  return (
    <div
      data-testid={`transaction-row-${txn.id}`}
      className={cn(
        'flex min-h-[44px] flex-col cursor-pointer overflow-x-hidden px-1 py-0.5 sm:px-1.5 sm:py-1',
        'transition-all duration-200 hover:bg-muted/30 active:bg-muted/50',
        showLongPressFeedback && 'ring-1 ring-primary/40 motion-safe:scale-[0.995]',
        isSelected && 'bg-primary/5',
      )}
      role="button"
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
      onPointerLeave={handlePointerEnd}
    >
      <div
        className={cn(
          // [wf-unified-slot] Checkbox slot reveal: grid-template-columns + opacity + transform (transform/opacity only per motion contract; grid column swap is the structural base).
          // Render ALWAYS so the checkbox stays keyboard-focusable mid-transition and aria-correct; visibility gates pointer access when hidden.
          // motion-reduce: no transform/opacity motion — element still mounts/hides without movement.
          'grid min-w-0 items-center gap-1 sm:gap-1.5',
          'motion-safe:transition-[grid-template-columns] motion-safe:duration-200 motion-safe:ease-out',
          selectionMode ? 'grid-cols-[22px_30px_minmax(0,1fr)_auto]' : 'grid-cols-[0px_30px_minmax(0,1fr)_auto]',
        )}
      >
        <div
          className={cn(
            'flex min-w-0 items-center justify-center overflow-hidden',
            // [wf-unified-slot] 200ms ease-out reveal; hidden state collapses via visibility+opacity+transform only (no display:none, no layout jump beyond the tracked grid column).
            'transition-[opacity,transform,visibility] duration-200 ease-out',
            'motion-reduce:transition-none',
            selectionMode
              ? 'visible translate-x-0 opacity-100'
              : 'invisible -translate-x-2 opacity-0 pointer-events-none',
          )}
          aria-hidden={!selectionMode}
          data-longpress-ignore="true"
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            checked={!!isSelected}
            onCheckedChange={() => onToggleSelect?.(txn.id)}
            aria-label={`Select ${txn.description}`}
            tabIndex={selectionMode ? 0 : -1}
            className="h-5 w-5"
            data-testid={`select-txn-${txn.id}`}
          />
        </div>

        <div
          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
            txn.emoji ? 'bg-muted' : `bg-muted ${config.color}`
          }`}
        >
          {txn.emoji ? (
            <span className="text-[15px] leading-none">{txn.emoji}</span>
          ) : (
            <Icon className="h-4 w-4" />
          )}
        </div>

        <div className="min-w-0">
          <div className="flex flex-col gap-0">
            <div className="text-sm font-medium leading-snug break-words" data-testid={`txn-primary-line-${txn.id}`}>
              {txn.description}
            </div>

            {showNotes && txn.notes && (
              <div
                className="text-xs leading-[1.05rem] text-muted-foreground italic break-words"
                data-testid={`txn-notes-cell-${txn.id}`}
              >
                {txn.notes}
              </div>
            )}
          </div>

          <div className="mt-px flex flex-wrap items-center gap-x-1 gap-y-0.5 text-[11px] leading-4 text-muted-foreground sm:text-xs" data-testid={`txn-meta-line-${txn.id}`}>
            {isColVisible('date') && (
              <span className="whitespace-nowrap">{formatDate(txn.date, 'short')}</span>
            )}

            {isColVisible('type') && (
              <Badge variant="outline" className={`${config.badge} text-[10px] leading-none px-1.5 py-0`}>
                {txn.type}
              </Badge>
            )}

            {isColVisible('category') && categoryName && (
              <span className="inline-flex min-w-0 items-center gap-1">
                {catEmoji ? (
                  <span
                    className="inline-flex h-4 w-4 rounded-full items-center justify-center text-[10px] shrink-0"
                    style={{ backgroundColor: catColor ? `${catColor}20` : '#e5e7eb' }}
                  >
                    {catEmoji}
                  </span>
                ) : null}
                <span className="break-words">{categoryName}</span>
              </span>
            )}

            {isColVisible('account') && matchedAccount && (
              <span className="inline-flex min-w-0 items-center gap-1 break-words">
                {matchedAccount.emoji && <span className="text-[11px] leading-none">{matchedAccount.emoji}</span>}
                {matchedAccount.name}
              </span>
            )}

            {isColVisible('recurring') && txn.isRecurring && (
              <Badge variant="outline" className="text-[10px] leading-none px-1.5 py-0">Recurring</Badge>
            )}

            {!showLineItems && hasItemizedData && (
              <Badge
                variant="secondary"
                className="h-4 px-1.5 py-0 text-[10px] leading-none"
                data-testid={`txn-itemized-chip-${txn.id}`}
              >
                <span className="inline-flex items-center" data-testid={`txn-itemized-indicator-${txn.id}`}>
                  <Layers className="h-3 w-3 mr-0.5" />
                  {showItemCount !== false && hasItemRows
                    ? `${txn.items!.length} item${txn.items!.length !== 1 ? 's' : ''}`
                    : 'Itemized'}
                </span>
              </Badge>
            )}
          </div>

          {showTags && txn.tags && txn.tags.length > 0 && (
            <div className="mt-0.5 flex flex-wrap gap-1">
              {txn.tags.slice(0, 3).map((tag) => (
                <Badge key={tag} variant="outline" className="text-[10px] px-1.5 py-0 h-4 leading-none">
                  {tag}
                </Badge>
              ))}
              {txn.tags.length > 3 && (
                <span className="text-[10px] text-muted-foreground leading-4">+{txn.tags.length - 3} more</span>
              )}
            </div>
          )}
        </div>

        <div className="flex shrink-0 items-center justify-end gap-0.5 text-right" data-testid={`txn-amount-cluster-${txn.id}`}>
          <div className="min-w-[4.25rem] space-y-0.5 tabular-nums leading-tight sm:min-w-[4.9rem]">
            <div
              className={cn(
                'text-sm font-semibold whitespace-nowrap',
                isNegativeNet ? 'text-red-600' : 'text-green-600',
              )}
              data-testid={`txn-net-${txn.id}`}
            >
              {isNegativeNet ? '-' : ''}{fmt(amountBreakdown.net)}
            </div>
            {showDiscountBreakdown && (
              <div
                className="flex flex-col items-end gap-0.5 text-muted-foreground"
                data-testid={`txn-secondary-amounts-${txn.id}`}
              >
                <span
                  className="whitespace-nowrap text-[10px] sm:text-[11px] leading-none"
                  data-testid={`txn-gross-${txn.id}`}
                  title="Gross amount"
                >
                  {fmt(amountBreakdown.gross)}
                </span>
                <span
                  className="whitespace-nowrap text-[10px] sm:text-[11px] leading-none text-emerald-600 dark:text-emerald-400"
                  data-testid={`txn-discount-${txn.id}`}
                  title="Discount applied"
                >
                  {fmt(effectiveDiscount)}
                </span>
              </div>
            )}
          </div>

          {!selectionMode && (
            <div
              className="flex items-center gap-0.5"
              data-longpress-ignore="true"
              data-testid={`transaction-actions-${txn.id}`}
              onClick={(e) => e.stopPropagation()}
            >
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-11 w-11 min-h-[44px] min-w-[44px] md:h-8 md:w-8 md:min-h-0 md:min-w-0"
                    aria-label="Actions"
                  >
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-40" collisionPadding={8}>
                  <DropdownMenuItem onClick={() => onView(txn)}>
                    <Eye className="h-3.5 w-3.5 mr-2" />
                    View Details
                  </DropdownMenuItem>
                  {onDuplicate && (
                    <DropdownMenuItem onClick={() => onDuplicate(txn)}>
                      <Copy className="h-3.5 w-3.5 mr-2" />
                      Duplicate
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => onDelete(txn.id)} className="text-destructive focus:text-destructive">
                    <Trash2 className="h-3.5 w-3.5 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {showLineItems && hasItemRows && !onOpenItemsSheet && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setDesktopItemsExpanded(!desktopItemsExpanded); }}
                  className={cn(
                    'inline-flex min-h-[44px] min-w-[44px] items-center justify-center gap-1 rounded-md px-2 text-xs font-medium md:min-h-0 md:min-w-0 md:h-8',
                    'bg-primary/10 text-primary hover:bg-primary/15',
                    'border border-primary/20',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                    'motion-safe:transition-colors motion-safe:duration-150',
                  )}
                  data-testid={`desktop-toggle-items-${txn.id}`}
                  aria-expanded={desktopItemsExpanded}
                  aria-label={`${txn.items!.length} line items — click to ${desktopItemsExpanded ? 'collapse' : 'expand'}`}
                >
                  <Layers className="h-3.5 w-3.5 opacity-80" />
                  <ChevronDown
                    className={cn(
                      'h-3 w-3 motion-safe:transition-transform motion-safe:duration-200',
                      desktopItemsExpanded ? 'rotate-180' : 'rotate-0',
                    )}
                  />
                  <span className="hidden sm:inline">{txn.items!.length}</span>
                </button>
              )}

              {showLineItems && hasItemRows && onOpenItemsSheet && (
                <button
                  type="button"
                  onClick={() => onOpenItemsSheet(txn)}
                  className={cn(
                    'inline-flex min-h-[44px] min-w-[44px] items-center justify-center gap-1 rounded-md px-2 text-xs font-medium md:min-h-0 md:min-w-0 md:h-8',
                    'bg-primary/10 text-primary hover:bg-primary/15',
                    'border border-primary/20',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                    'transition-colors duration-150',
                  )}
                  data-testid={`mobile-open-items-${txn.id}`}
                  aria-label={`View ${txn.items!.length} line items`}
                >
                  <Layers className="h-3.5 w-3.5 opacity-80" />
                  <span className="hidden sm:inline">
                    {txn.items!.length}
                  </span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {showLineItems && hasItemRows && !onOpenItemsSheet && (
        <div
          className={cn(
            'grid min-w-0 motion-safe:transition-[grid-template-rows] motion-safe:duration-300 motion-safe:ease-in-out',
            desktopItemsExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
          )}
        >
          <div
            className={cn(
              'min-h-0 overflow-hidden pl-0 pr-1',
              'motion-safe:transition-opacity motion-safe:duration-200',
              desktopItemsExpanded
                ? 'motion-safe:opacity-100 motion-safe:delay-100 pb-2'
                : 'motion-safe:opacity-0',
            )}
          >
            <TransactionLineItems
              items={txn.items!}
              transactionAmount={txn.amount}
              transactionDiscountTotal={txn.discountAmount ?? 0}
              showSummaryNetTotal={false}
              hideToggle={true}
            />
          </div>
        </div>
      )}
    </div>
  );
}
